import { AnimatePresence, motion, PanInfo } from "framer-motion";
import React, { useCallback, useEffect, useRef, ReactNode } from "react";
import { StyledItem } from "./Styled";
import { oneOf, node, number, object, func } from "prop-types";

interface CarouselItemState {
  active: number;
  next: boolean;
  prevActive: number;
}

interface CarouselItemProps {
  animation: "slide" | "fade";
  next: () => void;
  prev: () => void;
  swipe: boolean;
  state: CarouselItemState;
  index: number;
  maxIndex: number;
  duration: number;
  child: ReactNode;
  height: number | string;
  setHeight: (height: number) => void;
}

export const CarouselItem = ({
  animation,
  next,
  prev,
  swipe,
  state,
  index,
  maxIndex,
  duration,
  child,
  height,
  setHeight,
}: CarouselItemProps) => {
  const slide = animation === "slide";
  const fade = animation === "fade";

  const dragProps = {
    drag: "x" as const,
    layout: true,
    onDragEnd: (
      event: MouseEvent | TouchEvent | PointerEvent,
      info: PanInfo,
    ) => {
      if (!swipe) return;
      if (info.offset.x > 0) prev && prev();
      else if (info.offset.x < 0) next && next();
      (event as any).stopPropagation();
    },
    dragElastic: 0,
    dragConstraints: { left: 0, right: 0 },
  };

  const divRef = useRef(null);

  const checkAndSetHeight = useCallback(
    function check() {
      if (index !== state.active) return;
      if (!divRef.current) return;

      if (divRef.current.offsetHeight === 0) {
        setTimeout(check, 100);
      } else {
        setHeight(divRef.current.offsetHeight);
      }
    },
    [setHeight, state.active, index],
  );

  useEffect(() => {
    checkAndSetHeight();
  }, [checkAndSetHeight]);

  const variants = {
    leftwardExit: {
      x: slide ? "-100%" : undefined,
      opacity: fade ? 0 : undefined,
      zIndex: 0,
    },
    leftOut: {
      x: slide ? "-100%" : undefined,
      opacity: fade ? 0 : undefined,
      display: "none",
      zIndex: 0,
    },
    rightwardExit: {
      x: slide ? "100%" : undefined,
      opacity: fade ? 0 : undefined,
      zIndex: 0,
    },
    rightOut: {
      x: slide ? "100%" : undefined,
      opacity: fade ? 0 : undefined,
      display: "none",
      zIndex: 0,
    },
    center: {
      x: 0,
      opacity: 1,
      zIndex: 1,
    },
  };

  const { active, next: isNext, prevActive } = state;
  let animate = "center";

  if (index === prevActive) {
    animate = isNext ? "leftwardExit" : "rightwardExit";
    if (active === maxIndex && index === 0) animate = "rightwardExit";
    if (active === 0 && index === maxIndex) animate = "leftwardExit";
  } else if (index !== active) {
    animate = index < active ? "leftOut" : "rightOut";
    if (active === maxIndex && index === 0) animate = "rightOut";
    if (active === 0 && index === maxIndex) animate = "leftOut";
  }

  const transitionDuration = duration / 1000;

  return (
    <StyledItem>
      <AnimatePresence custom={isNext}>
        <motion.div {...(swipe && dragProps)} style={{ height: "100%" }}>
          <motion.div
            custom={isNext}
            variants={variants}
            animate={animate}
            transition={{
              x: { type: "tween", duration: transitionDuration, delay: 0 },
              opacity: { duration: transitionDuration },
            }}
            style={{ position: "relative", height: "100%" }}
          >
            <div ref={divRef} style={{ height: height }}>
              {child}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </StyledItem>
  );
};

CarouselItem.propTypes = {
  animation: oneOf(["slide", "fade"]).isRequired,
  next: node,
  prev: node,
  swipe: number,
  state: object,
  index: number,
  maxIndex: number,
  duration: number,
  child: node,
  height: number,
  setHeight: func.isRequired,
};
